import React, { FC, ReactNode, createContext, useEffect, useState } from 'react';
import { BASE_FONT_SIZE_KEY, BASE_FONT_SIZE_STEPS_PX, DEFAULT_BASE_FONT_SIZE_PX, GRAYSCALE_THEME_KEY } from '@constants';
import { loadFromLocalStorage, saveToLocalStorage } from '@utils/storage';

const globalStylesForGrayscaleTheme = `
    /* MAKE ALL IMAGES GRAYSCALE */
    img {
        filter: grayscale(1) !important;
    }
    div[style*="background-image"] {
        filter: grayscale(1) !important;
    }
`;

type ThemesContextProps = {
    // high contrast theme
    useGrayscaleTheme: boolean;
    toggleGrayscaleTheme: () => void;

    // font size
    increaseFontSize: () => void;
    decreaseFontSize: () => void;
};

export const ThemesContext = createContext<ThemesContextProps>({
    // high contrast theme
    useGrayscaleTheme: false,
    toggleGrayscaleTheme: () => {},

    // font size
    increaseFontSize: () => {},
    decreaseFontSize: () => {},
});

type ThemesContextProvider = {
    children: ReactNode;
};

/**
 * The information of templates being used is stored in local storage.
 */
export const ThemesProvider: FC<ThemesContextProvider> = ({ children }) => {
    // high contrast theme
    const [useGrayscaleTheme, setUseGrayscaleTheme] = useState<boolean | null>(null);

    useEffect(() => {
        if (useGrayscaleTheme === null) {
            const initialValue = loadFromLocalStorage<string>(GRAYSCALE_THEME_KEY) === 'true';
            setUseGrayscaleTheme(initialValue);
        } else {
            saveToLocalStorage<string>(GRAYSCALE_THEME_KEY, JSON.stringify(useGrayscaleTheme));
        }
    }, [useGrayscaleTheme]);

    // font size
    const [baseFontSize, setBaseFontSize] = useState<number | null>(null);

    useEffect(() => {
        if (baseFontSize === null) {
            const initialValue = Number(loadFromLocalStorage<string>(BASE_FONT_SIZE_KEY) || DEFAULT_BASE_FONT_SIZE_PX);
            setBaseFontSize(initialValue);
        } else {
            saveToLocalStorage<string>(BASE_FONT_SIZE_KEY, JSON.stringify(baseFontSize));
        }
    }, [baseFontSize]);

    const getCurrentFontSizeStep = () => {
        let currentStep = BASE_FONT_SIZE_STEPS_PX.indexOf(baseFontSize || DEFAULT_BASE_FONT_SIZE_PX);
        // in case the local storage information no longer matches any item of BASE_FONT_SIZE_STEPS_PX
        if (currentStep === -1) {
            currentStep = BASE_FONT_SIZE_STEPS_PX.indexOf(DEFAULT_BASE_FONT_SIZE_PX);
        }
        return currentStep;
    };

    const contextValue: ThemesContextProps = {
        // high contrast theme
        useGrayscaleTheme: useGrayscaleTheme || false,
        toggleGrayscaleTheme: () => {
            setUseGrayscaleTheme(!useGrayscaleTheme);
        },

        // font size
        increaseFontSize: () => {
            const currentStep = getCurrentFontSizeStep();
            if (currentStep < BASE_FONT_SIZE_STEPS_PX.length - 1) {
                setBaseFontSize(BASE_FONT_SIZE_STEPS_PX[currentStep + 1]);
            }
        },
        decreaseFontSize: () => {
            const currentStep = getCurrentFontSizeStep();
            if (currentStep > 0) {
                setBaseFontSize(BASE_FONT_SIZE_STEPS_PX[currentStep - 1]);
            }
        },
    };

    return (
        <ThemesContext.Provider value={contextValue}>
            <style>{`body {font-size: ${baseFontSize}px}`}</style>
            {useGrayscaleTheme && <style>{globalStylesForGrayscaleTheme}</style>}
            {children}
        </ThemesContext.Provider>
    );
};
